import React, { lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatedSection } from '../components/animated/AnimatedSection';
import { MagneticButton } from '../components/animated/MagneticButton';
import { FeatureCard } from '../components/animated/FeatureCard';
import { courses } from '../data/courses';
import { BookOpen, Award, Users, Sparkles, TrendingUp, Lightbulb, Clock, MapPin } from 'lucide-react';
export function AcademyPage() {
  const navigate = useNavigate();
  const learningPathways = [{
    icon: <Award className="w-7 h-7 text-blue-600" data-id="element-684" />,
    title: 'Executive Courses',
    description: 'Advanced programs for senior leaders and executives looking to enhance strategic thinking and leadership capabilities.'
  }, {
    icon: <Sparkles className="w-7 h-7 text-green-600" data-id="element-685" />,
    title: 'Creative Industry Courses',
    description: 'Specialized training for creative professionals in media, design, and digital content creation.'
  }, {
    icon: <BookOpen className="w-7 h-7 text-blue-600" data-id="element-686" />,
    title: 'Professional Certifications',
    description: 'Industry-recognized certifications that validate your expertise and boost career prospects.'
  }, {
    icon: <Users className="w-7 h-7 text-green-600" data-id="element-687" />,
    title: 'Soft-Skills Training',
    description: 'Essential interpersonal and communication skills for professional success in any field.'
  }, {
    icon: <TrendingUp className="w-7 h-7 text-blue-600" data-id="element-688" />,
    title: 'Youth Power Skills',
    description: 'Foundational skills for young professionals entering the workforce or starting businesses.'
  }, {
    icon: <Lightbulb className="w-7 h-7 text-green-600" data-id="element-689" />,
    title: 'Digital Skills & Technology Programmes',
    description: 'Cutting-edge tech skills including digital literacy, data analysis, and emerging technologies.'
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-690">
      <main className="flex-grow pt-20" data-id="element-691">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden" data-id="element-692">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-green-50" data-id="element-693" />
          <div className="absolute top-20 right-10 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20" data-id="element-694" />

          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-695">
            <div className="max-w-4xl mx-auto text-center" data-id="element-696">
              <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-8" data-id="element-697">
                SkillzHQ{' '}
                <span className="bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent" data-id="element-698">
                  Academy
                </span>
              </h1>
              <p className="text-2xl text-gray-600 leading-relaxed mb-6" data-id="element-699">
                Building Africa's talent pipeline
              </p>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto" data-id="element-700">
                SkillzHQ Academy offers flexible, practical, and affordable
                learning experiences designed to equip learners with the skills
                needed for today's economy.
              </p>
            </div>
          </div>
        </section>

        {/* Learning Pathways */}
        <AnimatedSection className="py-24 bg-white" data-id="element-701">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-702">
            <div className="max-w-6xl mx-auto" data-id="element-703">
              <div className="text-center mb-16" data-id="element-704">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-705">
                  Our Learning Pathways
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-706">
                  Every course is designed by experts, delivered through a
                  modern, user-friendly platform, and built to ensure real-world
                  application.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-707">
                {learningPathways.map((pathway, index) => <FeatureCard key={index} icon={pathway.icon} title={pathway.title} description={pathway.description} data-id="element-708" />)}
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* Featured Courses Section */}
        <AnimatedSection className="py-24 bg-gradient-to-b from-gray-50 to-white" data-id="element-709">
          <div className="container mx-auto px-6 lg:px-8" data-id="element-710">
            <div className="max-w-6xl mx-auto" data-id="element-711">
              <div className="text-center mb-16" data-id="element-712">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" data-id="element-713">
                  Featured Courses
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-id="element-714">
                  Our courses empower learners to grow regardless of age or
                  background.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-715">
                {courses.map(course => <div key={course.id} className="group bg-white rounded-2xl overflow-hidden border-2 border-gray-100 hover:border-blue-300 hover:shadow-2xl transition-all duration-500 cursor-pointer hover:-translate-y-2" onClick={() => navigate(`/courses/${course.id}`)} data-id="element-716">
                    <div className="relative h-48 overflow-hidden" data-id="element-717">
                      <img src={course.image} alt={course.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" data-id="element-718" />
                    </div>

                    <div className="p-6" data-id="element-719">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300" data-id="element-720">
                        {course.title}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed line-clamp-2" data-id="element-721">
                        {course.description}
                      </p>

                      <div className="flex flex-wrap gap-4 mb-4" data-id="element-722">
                        <div className="flex items-center gap-2 text-gray-600" data-id="element-723">
                          <Clock className="w-4 h-4 text-blue-600" data-id="element-724" />
                          <span className="text-sm" data-id="element-725">{course.duration}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600" data-id="element-726">
                          <MapPin className="w-4 h-4 text-green-600" data-id="element-727" />
                          <span className="text-sm" data-id="element-728">Virtual</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-200" data-id="element-729">
                        <div data-id="element-730">
                          <div className="text-2xl font-bold text-gray-900" data-id="element-731">
                            {course.price}
                          </div>
                          <div className="text-sm text-gray-600" data-id="element-732">
                            {course.priceUSD}
                          </div>
                        </div>
                        <MagneticButton variant="primary" size="sm" data-id="element-733">
                          Learn More
                        </MagneticButton>
                      </div>
                    </div>
                  </div>)}
              </div>

              <div className="text-center" data-id="element-734">
                <p className="text-2xl font-semibold text-gray-900 mb-8" data-id="element-735">
                  Learn at your pace. Grow at your potential.
                </p>
                <MagneticButton variant="primary" size="lg" onClick={() => navigate('/courses')} data-id="element-736">
                  Explore All Courses
                </MagneticButton>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection className="py-32 bg-gradient-to-br from-blue-600 to-green-600 relative overflow-hidden" data-id="element-737">
          <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]" data-id="element-738" />
          <div className="container mx-auto px-6 lg:px-8 relative z-10" data-id="element-739">
            <div className="max-w-4xl mx-auto text-center" data-id="element-740">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8" data-id="element-741">
                Ready to Start Learning?
              </h2>
              <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto" data-id="element-742">
                Join thousands of learners transforming their careers with
                SkillzHQ Academy
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-743">
                <MagneticButton variant="secondary" size="lg" onClick={() => navigate('/courses')} data-id="element-744">
                  Browse Courses
                </MagneticButton>
                <MagneticButton variant="outline" size="lg" onClick={() => navigate('/contact')} data-id="element-745">
                  Get in Touch
                </MagneticButton>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </main>
    </div>;
}